/**
 * API-клиент виджета «Оплаты».
 *
 * Обязанности:
 *  - хранит access-JWT В ПАМЯТИ (не localStorage/cookie — iframe кросс-сайтовый, ITP);
 *  - подставляет Authorization: Bearer и X-CSRF-Token (double-submit, значение из embed);
 *  - разворачивает конверт ошибки { error: { code, message, details? } } → ApiError;
 *  - на 401 делает ОДИН silent-refresh (POST /api/auth/refresh, credentials:'include') и повтор;
 *  - поддерживает If-Match (оптимистичная блокировка) и Idempotency-Key на мутациях;
 *  - all запросы credentials:'include' (для refresh-cookie на 401-ретрае).
 *
 * Базовый URL: import.meta.env.VITE_API_BASE_URL (по умолчанию '' — same-origin). Пути ниже
 * задаются относительно него и уже включают префикс `/api`.
 *
 * Сверено с backend: auth.controller.ts (embed/refresh, cookie refresh_token, header x-csrf-token),
 * http-exception.filter.ts (конверт ошибки), payments.controller.ts (If-Match), main.ts (CORS allowedHeaders).
 */

import type { ApiErrorBody, RefreshResponse } from './types';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

const CSRF_HEADER = 'X-CSRF-Token';

/** Типизированная ошибка API. `code` — машинный код из конверта; `status` — HTTP-код. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** 409 несовпадения бюджета (finalize/template). */
  get isBudgetMismatch(): boolean {
    return this.status === 409 && this.code === 'BUDGET_MISMATCH';
  }

  /** 409 конфликта версий (устаревший If-Match). */
  get isVersionConflict(): boolean {
    return this.status === 409 && this.code === 'VERSION_CONFLICT';
  }

  /** 428 — обязательный If-Match отсутствует (edit_paid-ресурс). */
  get isPreconditionRequired(): boolean {
    return this.status === 428;
  }

  /** Недостаточно прав. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

// ---------------------------------------------------------------------------
// Токен-хранилище (в памяти модуля)
// ---------------------------------------------------------------------------

let accessToken: string | null = null;
let csrfToken: string | null = null;
/** Опциональный refresh-токен (fallback, если cookie не доедет в iframe). */
let refreshToken: string | null = null;

/** Одна in-flight операция refresh — чтобы параллельные 401 не жгли refresh многократно. */
let refreshing: Promise<boolean> | null = null;

/** Установить токены после embed/refresh. */
export function setAuthTokens(tokens: {
  accessToken: string;
  csrfToken?: string;
  refreshToken?: string;
}): void {
  accessToken = tokens.accessToken;
  if (tokens.csrfToken !== undefined) csrfToken = tokens.csrfToken;
  if (tokens.refreshToken !== undefined) refreshToken = tokens.refreshToken;
}

/** Сбросить токены (logout / фатальная ошибка авторизации). */
export function clearAuthTokens(): void {
  accessToken = null;
  csrfToken = null;
  refreshToken = null;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Абсолютный URL API-эндпоинта. Пути передаются уже с префиксом `/api` (например `/api/deals/...`).
 */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

// ---------------------------------------------------------------------------
// refresh
// ---------------------------------------------------------------------------

/**
 * Silent-refresh access-токена. Возвращает true при успехе.
 * Refresh-токен в cross-site iframe обычно в HttpOnly-cookie (credentials:'include');
 * если он есть в памяти (fallback) — дублируем в теле.
 */
async function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async (): Promise<boolean> => {
    try {
      const res = await fetch(apiUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { [CSRF_HEADER]: csrfToken } : {}),
        },
        body: JSON.stringify(refreshToken ? { refreshToken } : {}),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as RefreshResponse;
      accessToken = body.accessToken;
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/**
 * Публичный форсированный refresh — для проактивного обновления токена по таймеру (useAuth),
 * до того как истечёт TTL и словится 401. Дедуплицируется с 401-refresh через ту же in-flight-промис.
 */
export function forceRefreshAccessToken(): Promise<boolean> {
  return refreshAccessToken();
}

// ---------------------------------------------------------------------------
// Ядро запроса
// ---------------------------------------------------------------------------

/** Методы, для которых имеет смысл слать Idempotency-Key (создающие/проводящие мутации). */
const IDEMPOTENT_POST = /\/(payments|transactions|template|finalize)$/;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Тело — сериализуется в JSON, если это объект. */
  body?: unknown;
  /** Query-параметры (массивы кодируются как CSV: ['A','B'] → "A,B"). */
  query?: Record<string, string | number | boolean | string[] | undefined>;
  /** Значение version для заголовка If-Match (оптимистичная блокировка). */
  ifMatch?: number | string;
  /** UUID идемпотентности (генерируется автоматически для create-мутаций, если не задан). */
  idempotencyKey?: string;
  /** Доп. заголовки. */
  headers?: Record<string, string>;
  /** Отдать тело как Blob (для экспорта). */
  responseType?: 'json' | 'blob' | 'void';
  /** Внутренний флаг: запрет повторного retry после refresh. */
  _isRetry?: boolean;
  /** AbortSignal для отмены. */
  signal?: AbortSignal;
}

function buildQuery(query: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Простой генератор UUID v4 (crypto.randomUUID есть во всех целевых браузерах iframe). */
function uuidv4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback (крайне редкий): псевдо-UUID, достаточно для идемпотентности в рамках сессии.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function parseError(res: Response): Promise<ApiError> {
  let code = defaultCodeForStatus(res.status);
  let message = res.statusText || 'Request failed';
  let details: unknown;
  let requestId: string | undefined;
  try {
    const body = (await res.json()) as Partial<ApiErrorBody> & Record<string, unknown>;
    if (body && typeof body === 'object' && 'error' in body && body.error && typeof body.error === 'object') {
      const e = body.error as ApiErrorBody['error'];
      code = e.code ?? code;
      message = e.message ?? message;
      details = e.details;
      requestId = e.requestId;
    }
  } catch {
    /* тело не JSON — оставляем дефолты */
  }
  return new ApiError(res.status, code, message, details, requestId);
}

function defaultCodeForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'BAD_REQUEST';
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 422:
      return 'UNPROCESSABLE_ENTITY';
    case 428:
      return 'PRECONDITION_REQUIRED';
    case 429:
      return 'RATE_LIMITED';
    default:
      return 'ERROR';
  }
}

/**
 * Основной низкоуровневый запрос. Возвращает распарсенный JSON (T), Blob или undefined.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const url = apiUrl(path) + buildQuery(options.query);
  const responseType = options.responseType ?? 'json';

  const headers: Record<string, string> = { ...options.headers };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (csrfToken && method !== 'GET') headers[CSRF_HEADER] = csrfToken;

  let payload: BodyInit | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(options.body);
  }

  if (options.ifMatch !== undefined && !('If-Match' in headers)) {
    headers['If-Match'] = `"${options.ifMatch}"`;
  }

  if (method === 'POST' && IDEMPOTENT_POST.test(path.split('?')[0] ?? path)) {
    headers['Idempotency-Key'] = options.idempotencyKey ?? uuidv4();
  }

  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: payload,
    signal: options.signal,
  });

  // 401 → один silent-refresh + повтор.
  if (res.status === 401 && !options._isRetry) {
    const ok = await refreshAccessToken();
    if (ok) {
      return request<T>(path, { ...options, _isRetry: true });
    }
  }

  if (!res.ok) {
    throw await parseError(res);
  }

  if (responseType === 'void' || res.status === 204) {
    return undefined as T;
  }
  if (responseType === 'blob') {
    return (await res.blob()) as T;
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Удобные обёртки
// ---------------------------------------------------------------------------

export const apiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/**
 * Прямой вызов embed (без Bearer — токенов ещё нет). Отдельно от request(), т.к.
 * это точка входа авторизации; сам эндпоинт не под JwtAuthGuard.
 */
export async function postEmbed<T>(body: unknown): Promise<T> {
  const res = await fetch(apiUrl('/api/auth/embed'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await parseError(res);
  }
  return (await res.json()) as T;
}
